<template>
  <div class="admins">
    <div class="admins__sidebar">
      <div class="admins__sidebar-container">
        <h3 class="admins__sidebar-container-title">{{ $t('view.admins.activeTitle') }}</h3>
        <div class="admins__items">
          <div class="admins__item" v-for="(item, index) in admins" :key="index">
            <short-card
              v-model="admins[index].admin"
              @selected="getSelected(item)"
              :isSelected="isSelected(item)"
              v-if="item.admin.active"
            >
              <div>{{ item.admin.name }}</div>
              <div>{{ item.admin.email || $t('view.admins.noEmail') }}</div>
            </short-card>
          </div>
        </div>
      </div>
      <div class="admins__sidebar-container" v-if="inactiveAdmins.length">
        <h3 class="admins__sidebar-container-title">{{ $t('view.admins.inactiveTitle') }}</h3>
        <div class="admins__items">
          <div class="admins__item" v-for="(item, index) in inactiveAdmins" :key="index">
            <short-card
              v-model="inactiveAdmins[index]"
              @selected="getSelected(item)"
              :isSelected="isSelected(item)"
              v-if="!item.active"
            >
              <div class="card__name">{{ item.admin.name }}</div>
              <div class="card__email">{{ item.admin.email || $t('view.admins.noEmail') }}</div>
            </short-card>
          </div>
        </div>
      </div>
      <span class="admins__sidebar-create" @click="showCreate">Add</span>
    </div>
    <div class="admins__editor editor" v-if="selected">
      <section-title v-if="createMode">{{ $t('view.admins.dataTitle') }}</section-title>
      <div v-if="createMode" class="admins__section editor-section">
        <div class="editor-section-description">{{ $t('view.admins.textes.basic') }}</div>
        <div class="admins__section admins__basic-section">
          <div class="admins__input-container">
            <in
              class="admins__input input-create-mode"
              v-model="selected.admin.name"
              :label="$t('view.admins.name')"
              @blur="onBlurInputValidation(validateName(), invalidAdmin.badNameFormat)"
            ></in>
            <in
              class="admins__input input-create-mode"
              v-model="selected.admin.username"
              :label="$t('view.admins.username')"
              @blur="onBlurInputValidation(validateUsername(), invalidAdmin.badUsernameFormat)"
            ></in>
            <in
              v-if="createMode"
              type="password"
              passwordAutofillDisabled
              class="admins__input input-create-mode input-create-mode__margins"
              v-model="selected.admin.password"
              :label="$t('view.admins.password')"
            ></in>
            <in
              class="admins__input input-create-mode input-create-mode__margins"
              v-model="selected.admin.email"
              :label="$t('view.admins.email')"
              @blur="onBlurInputValidation(validateEmail(), invalidAdmin.badEmailFormat)"
              @enter="validateNewAdmin"
            ></in>
          </div>
          <div class="admins__validate-text">{{ invalidAdminText }}</div>
          <btn
            v-if="!createMode"
            class="admins__basic-section-button"
            icon="save"
            @click="saveEditedAdmin"
          >{{ $t('btn.save') }}</btn>
        </div>
      </div>

      <section-title>{{ $t('view.admins.permissionsTitle') }}</section-title>
      <div class="admins__section admins__perm-section editor-section">
        <div
          class="editor-section-description"
        >{{ $t('view.admins.textes.permissions') }}</div>
        <div class="admins__perm-section">
          <sw-button
            v-for="(item, index) in selected.permissions"
            :key="index"
            class="admins__perm-section-switches"
            :label="$t(getPermissionText(index))"
            v-model="item.permitted"
          ></sw-button>
        </div>
        <btn
          v-if="!createMode"
          class="admins__basic-section-button"
          icon="save"
          @click="saveEditedPermissions"
        >{{ $t('btn.save') }}</btn>
        <btn
          v-if="createMode"
          class="admins__basic-section-button"
          icon="save"
          @click="validateNewAdmin"
        >{{ $t('btn.create') }}</btn>
      </div>

      <section-title v-if="!createMode">{{ $t('view.admins.generalTitle') }}</section-title>
      <div v-if="!createMode" class="admins__section admins__general-section editor-section">
        <div
          class="editor-section-description"
        >{{ $t('view.admins.textes.general') }}</div>
        <div class="admins__general-container">
          <div
            v-if="selfSelect"
            class="editor-section-description editor-section-description--important"
          >{{ $t('view.admins.textes.deactivateSelf') }}</div>
          <sw-button
            v-if="!createMode"
            :inactive="selfSelect"
            :label="$t('view.admins.active')"
            v-model="activeCopy"
          ></sw-button>
          <btn
            v-if="!createMode"
            :inactive="selfSelect"
            class="admins__basic-section-button"
            icon="save"
            @click="saveEditedAdmin"
          >{{ $t('btn.save') }}</btn>
        </div>
      </div>

      <section-title subsection v-if="!createMode">{{ $t('view.admins.deleteTitle') }}</section-title>
      <div v-if="!createMode" class="admins__section editor-section">
        <div class="editor-section-description">{{ $t('view.admins.textes.delete') }}</div>
        <div
          v-if="!selfSelect"
          class="editor-section-description editor-section-description--important"
        >{{ $t('view.admins.textes.deleteImportant') }}</div>
        <div
          v-else
          class="editor-section-description editor-section-description--important"
        >{{ $t('view.admins.textes.deleteSelf') }}</div>
        <btn
          :inactive="selfSelect"
          color="red"
          class="admins__general-section-delete"
          @click="deleteAdmin"
          icon="trash-alt"
        >{{ $t('btn.delete') }}</btn>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'
export default {
  data () {
    return {
      admins: [],
      selected: null,
      createMode: false,
      permissionsTemplate: [],
      activeCopy: null,

      invalidAdmin: {
        badNameFormat: {
          text: this.$t('validation.admin.format.name'),
          flag: false
        },
        badUsernameFormat: {
          text: this.$t('validation.admin.format.username'),
          flag: false
        },
        badEmailFormat: {
          text: this.$t('validation.admin.format.email'),
          flag: false
        }
      }
    }
  },

  mounted () {
    this.loadAdmins()
  },

  computed: {
    inactiveAdmins: function () {
      return this.admins.filter(a => !a.admin.active)
    },

    selfSelect: function () {
      return this.$store.state.user.admin.id === this.selected.admin.id
    },

    invalidAdminText: function () {
      let text = ''
      const keys = Object.keys(this.invalidAdmin)
      for (const key of keys) {
        if (this.invalidAdmin[key].flag) {
          text += this.invalidAdmin[key].text + ' '
        }
      }
      return text
    }
  },

  methods: {
    onBlurInputValidation (validationResult, toSet) {
      if (validationResult) toSet.flag = false
      else toSet.flag = true
    },

    //// FIELDS
    validateName () {
      if(!this.selected.admin.name) return true
      const reg = /^[a-ząćęłńóśźżA-ZĄĆĘŁŃÓŚŹŻ]+( [a-ząćęłńóśźżA-ZĄĆĘŁŃÓŚŹŻ]+)*$/
      if (reg.test(this.selected.admin.name) && this.selected.admin.name.length<32) return true
      return false
    },

    validateUsername () {
      if(!this.selected.admin.username) return true
      const reg = /^[a-zA-Z]+( [a-zA-Z]+)*$/
      if (reg.test(this.selected.admin.username) && this.selected.admin.username.length<32) return true
      return false
    },

    validateEmail () {
      if(!this.selected.admin.email) return true
      const reg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return reg.test(this.selected.admin.email.toLowerCase())
    },

    //// SECTIONS
    validateNewAdmin () {
      if(!this.selected.admin.name || !this.selected.admin.username || !this.selected.admin.password) {
        this.notify('error', 'validation.admin.empty.edit')
        return
      }
      if(!this.validateName() || !this.validateUsername() || !this.validateEmail()) {
        this.notify('error', 'validation.admin.format.edit')
        return
      }
      this.createNewAdmin()
    },

    async loadAdmins () {
      try {
        let res = await axios.get('/api/admins/list')
        let res2 = await axios.get('/api/admins/permissions/list')
        this.permissionsTemplate = res2.data.permissions
        this.admins = res.data.adminsCombined
        this.setStartSelected()
      } catch (e) {
        console.error(e)
      }
    },

    setStartSelected () {
      if (!this.admins.length) return
      for (let i = 0; i < this.admins.length; i++) {
        if (this.admins[i].admin.active) {
          this.selected = this.admins[i]
        }
      }
      if (!this.selected) this.selected = this.admins[0]
      if(this.selected.admin.active) this.activeCopy = true
      else this.activeCopy = false
    },

    isSelected (item) {
      if (item == this.selected) return true
    },

    getSelected (item) {
      if (this.selected == item) return
      this.selected = item
      if(item.admin.active) this.activeCopy = true
      else this.activeCopy = false
      this.activeCopy
      this.createMode = false
    },

    getPermissionText (index) {
      return this.permissionsTemplate[index].name
    },

    async saveEditedAdmin () {
      try {
        if(this.activeCopy) this.selected.admin.active = true
        else this.selected.admin.active = false
        await axios.put('/api/admins/update', {
          data: {
            admins: [this.selected.admin]
          }
        })
        this.notify('success', 'view.admins.success.edit')
      } catch (e) {
        console.error(e)
        this.notify('error', 'view.admins.error.edit')
      }
    },

    async saveEditedPermissions () {
      try {
        await axios.put('/api/admins/permissions/update', {
          data: {
            permissions: this.selected.permissions
          }
        })
        this.notify('success', 'view.admins.success.edit')
      } catch (e) {
        console.error(e)
        this.notify('error', 'view.admins.error.edit')
      }
    },

    async showCreate () {
      let createTemplate = {
        admin: {
          username: '',
          password: '',
          active: true,
          email: null,
          name: '',
          notification_requests_active: false,
          notification_statistics_active: false,
          notification_technical_active: false
        },
        permissions: []
      }
      
      for(let i = 0; i<this.permissionsTemplate.length; i++) {
        createTemplate.permissions.push({
          permitted: true,
          permissions_id: this.permissionsTemplate[i].id
        })
      }
      this.selected = createTemplate
      this.activeCopy = true
      this.createMode = true
    },

    async createNewAdmin () {
      try {
        let ids = await axios.post('/api/admins/insert', {
          data: {
            admins: [this.selected]
          }
        })

        this.selected.admin.id = ids.data.returnIds[0].adminId
        for (let i = 0; i < this.selected.permissions.length; i++) {
          this.selected.permissions[i].id = ids.data.returnIds[0].permissionsIds[i]
        }

        this.admins.push(this.selected)
        this.createMode = false
        this.notify('success', 'view.admins.success.create')
      } catch (e) {
        console.error(e)
        this.notify('error', 'view.admins.error.create')
      }
    },

    async deleteAdmin () {
      try {
        await axios.delete('/api/admins/delete', {
          data: {
            adminIds: [this.selected.admin.id]
          }
        })

        for (let i = 0; i < this.admins.length; i++) {
          if (this.admins[i] == this.selected) {
            this.admins.splice(i, 1)
            this.selected = this.admins[0]
            break
          }
        }
        if(this.selected.admin.active) this.activeCopy = true
        else this.activeCopy = false

        this.notify('success', 'view.admins.success.delete')
      } catch (e) {
        console.error(e)
        this.notify('error', 'view.admins.error.delete')
      }
    }
  }
}
</script>

<style lang="scss">
.admins {
  display: flex;
  width: 100%;

  &__sidebar {
    @include viewSidebar();
    overflow: auto;
    padding-bottom: 18px;
    height: calc(100% - 18px)
  }

  &__input-container {
    display: flex;
    flex-wrap: wrap;
  }

  &__input {
    width: 30%;
    margin-right: 3%;
  }

  &__section {
    margin-bottom: 80px;
    position: relative;
    width: 50%;
    min-width: 540px;
  }

  &__basic-section {
    display: flex;
    align-items: center;
  }

  &__perm-section-switches {
    width: 140px;
    margin-bottom: 10px;
  }

  &__basic-section-button {
    position: absolute;
    top: calc(100% + 18px);
    right: 0;
  }

  &__item {
    width: 92%;
    margin: 10px auto 10px auto;
  }

  &__sidebar-container-title {
    padding-left: 12px;
    margin-bottom: 5px;
  }

  &__sidebar-container {
    margin-bottom: 25px;
  }

  &__editor {
    flex-grow: 1;
  }

  &__sidebar-create {
    @include clickableText();
  }

  &__validate-text {
    position: absolute;
    top: calc(100% + 18px);
    left: calc(0px);
    text-transform: uppercase;
    font-weight: 500;
    font-family: "Work Sans";
    font-size: 0.7em;
    letter-spacing: 0.01em;
    color: $danger;
  }
}

.input-create-mode {
  width: 40%;
  &__margins {
    margin-top: 10px;
  }
}
</style>